<template>
  <app-dialog
    v-model="dialog"
    :value.sync="dialog"
    width="637"
    persistent
    content-class="v-dialog__form"
    @keydown.esc="dialog = false ">
    <v-card>
      <v-card-title>
        New Project
        <span
          class="sourcery__icon-wrapper black pointer"
          @click="dialog = false">
          <v-icon
            color="white"
            size="15">
            mdi-close
          </v-icon>
        </span>
      </v-card-title>
      <v-card-text>
        <v-form ref="createProjectForm">
          <v-text-field
            v-model="createProjectData.projectName"
            placeholder="Project Name*"
            dense
            single-line
            :error-messages="errorsMessages('projectName')"
            autocomplete="off" />

          <div
            v-if="dialog"
            class="d-flex align-items-center mt-6">
            <location-field
              placeholder="Project Location*"
              dense
              single-line
              :current-location="renderAddressText"
              :error-messages="errorsMessages('address')"
              :location-types="[TYPE_GEOCODE]"
              @setLocationData="createProjectData.location = $event"
              @setAddressData="createProjectData.address = $event" />
            <v-tooltip
              max-width="200"
              right>
              <template #activator="{ on }">
                <v-icon
                  color="black"
                  class="ml-2 pointer"
                  size="16"
                  v-on="on">
                  mdi-information
                </v-icon>
              </template>
              <span>
                Project names and locations are collected solely for internal data quality,
                verification, and improvement of accuracy. This information will never be
                disclosed to end users, all shared outputs exclude identifying project details.
              </span>
            </v-tooltip>
          </div>
          <v-text-field
            v-model="createProjectData.phase"
            placeholder="Project Phase"
            class="mt-6"
            single-line
            dense
            autocomplete="off" />
          <v-text-field
            v-model="createProjectData.number"
            placeholder="Project Number"
            class="mt-6"
            single-line
            dense
            autocomplete="off" />
          <v-text-field
            v-model.number="createProjectData.baseBudget"
            placeholder="Base budget"
            class="mt-6"
            type="number"
            single-line
            dense
            autocomplete="off"
            @keypress="isNumber" />
          <div class="d-flex">
            <v-text-field
              v-model.number="createProjectData.area"
              placeholder="Project area"
              class="mt-6"
              type="text"
              single-line
              dense
              autocomplete="off"
              @keypress="isNumber" />
            <ProjectsArea
              :radio-model="createProjectData.areaUnit"
              @updateRadioModel="createProjectData.areaUnit = $event" />
          </div>
          <v-text-field
            v-model.number="createProjectData.powerDensity"
            :placeholder="`Target Project Power Density (W/${areaUnitTitle })`"
            class="mt-6"
            type="number"
            single-line
            dense
            autocomplete="off" />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="blue"
          class="white--text"
          elevation="0"
          @click="createProject">
          Create
        </v-btn>
      </v-card-actions>
    </v-card>
  </app-dialog>
</template>
<script>
import {
  mapState, mapMutations, mapActions,
} from 'vuex';
import { required } from 'vuelidate/lib/validators';
import ProjectsApi from '@/services/graphql/projects';
import Validations from '@/utils/validations';
import { getAreaUnitTitleByValue } from '@/utils';
import { GET_TEXT_REASON } from '@/constants/userPermissions';
import ErrorsText from '@/constants/errors';
import ProjectsArea from '@/components/Projects/ProjectsArea';
import AppGoogleLocationField from '@/components/App/AppFields/AppGoogleLocationField';
import { TYPE_GEOCODE } from '@/constants/maps';
import { omit } from 'lodash';
export default {
  name: 'ProjectsCreateProjectModal',
  components: {
    ProjectsArea,
    locationField: AppGoogleLocationField,
  },
  data: () => ({
    createProjectData: {
      projectName: '',
      phase: '',
      number: '',
      address: '',
      location: '',
      baseBudget: '',
      area: '',
      powerDensity: '',
      areaUnit: '',
    },
    TYPE_GEOCODE,
  }),
  validations: {
    createProjectData: {
      projectName: {
        required,
      },
      address: {
        required,
      },
    },
  },
  computed: {
    ...mapState(['showCreateProjectModal']),
    ...mapState({
      showSpinner: state => state.showSpinner,
    }),
    ...mapState('Workspace', ['activeWorkspaceId']),
    renderAddress() {
      const { location, address } = this.createProjectData;
      const addressObject = {
        location,
        address,
      };
      const paramToOmit = address ? 'location' : 'address';
      return omit(addressObject, [paramToOmit]);
    },
    renderAddressText() {
      const { location, address } = this.createProjectData;
      return address?.formatted_address || location;
    },
    areaUnitTitle() {
      return getAreaUnitTitleByValue(this.createProjectData?.areaUnit);
    },
    dialog: {
      get() {
        return this.showCreateProjectModal;
      },
      set() {
        this.$store.commit('showModalAction', 'showCreateProjectModal');
      },
    },
  },
  watch: {
    dialog() {
      if (!this.dialog) {
        this.clearCreateForm();
      } else {
        this.getLibrariesList();
      }
    },
  },
  methods: {
    ...mapMutations(['spinner']),
    ...mapActions(['handleError']),
    ...mapActions({
      getLibrariesList: 'Libraries/getLibrariesList',
      setCreatedProject: 'Projects/setCreatedProject',
    }),
    GET_TEXT_REASON,
    errorsMessages(item) {
      const errors = [];
      if (!this.$v.createProjectData[item].$dirty) return errors;
      if (!this.$v.createProjectData[item].required) {
        errors.push(ErrorsText.ENTER_REQUIRED_VALUE);
      }
      return errors;
    },
    isNumber(evt) {
      Validations.isInteger(evt);
    },
    async createProject() {
      if (this.showSpinner) {
        return;
      }
      const workspaceId = this.activeWorkspaceId;
      this.$v.createProjectData.$touch();
      if (!this.$v.createProjectData.$invalid) {
        this.spinner(true);
        try {
          const {
            baseBudget,
            area,
            powerDensity,
            ...otherProjectData
          } = this.createProjectData;
          const payload = {
            ...omit(otherProjectData, ['location', 'address']),
            ...this.renderAddress,
            baseBudget: baseBudget || 0,
            area: area || 0,
            powerDensity: powerDensity || 0,
            workspaceId,
          };
          const deprecatedCreateProject = 'deprecatedCreateProjectV2';
          const { data } = await ProjectsApi[deprecatedCreateProject](payload);
          this.dialog = false;
          if (this.$route.name === 'projects') {
            let project = data.response;
            this.setCreatedProject(project);
          } else {
            this.$router.push({
              name: 'projects',
            });
          }
        } catch (err) {
          this.handleError(err);
          console.log(err);
        } finally {
          this.spinner(false);
        }
      }
    },
    clearCreateForm() {
      this.$refs.createProjectForm.reset();
      this.$v.createProjectData.$reset();
    },
  },
};
</script>
